import React, { useState } from 'react';
import { StepProps } from '../../types/onboarding';
import { CreateUserForm, Role } from '../../types';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { roleService } from '../../services/roleService';
import toast from 'react-hot-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import PhoneInput from '../forms/PhoneInput';
import { useOnboarding } from '../../contexts/OnboardingContext';

// Generate a temporary password for new users
const generateTempPassword = () => {
  return `Temp${Math.random().toString(36).slice(-8)}!`;
};

export const TeamStep: React.FC<StepProps> = ({ onNext, onSkip }) => {
  const [invitedUsers, setInvitedUsers] = useState<CreateUserForm[]>([]);
  const [currentUser, setCurrentUser] = useState<CreateUserForm>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    roleId: 0,
    password: generateTempPassword(),
  });
  const { refreshStatus } = useOnboarding();

  // Fetch roles using roleService (same as RolesPage)
  const { data: rolesResponse } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles,
  });

  const roles: Role[] = (rolesResponse as any)?.data || [];

  const createUserMutation = useMutation({
    mutationFn: async (data: CreateUserForm) => {
      const response = await apiService.createUser(data);
      if (!response.success) {
        throw new Error(response.message || 'Failed to invite user');
      }
      return response.data;
    },
    onSuccess: () => {
      setInvitedUsers([...invitedUsers, currentUser]);
      setCurrentUser({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        roleId: 0,
        password: generateTempPassword(),
      });
      refreshStatus();
      toast.success('User invited successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to invite user');
    },
  });

  const handleInviteUser = async () => {
    if (!currentUser.email.trim() || !currentUser.firstName.trim() || !currentUser.lastName.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!currentUser.roleId || currentUser.roleId === 0) {
      toast.error('Please select a role');
      return;
    }
    createUserMutation.mutate(currentUser);
  };

  const handleContinue = () => {
    onNext();
  };

  return (
    <div>
      <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        Invite Team Members (Optional)
      </h3>
      <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
        Add team members to collaborate on work orders. You can invite them now or later.
      </p>

      {invitedUsers.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Invited Users:</p>
          <div className="flex flex-wrap gap-2">
            {invitedUsers.map((user, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
              >
                {user.firstName} {user.lastName} ({user.email})
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={currentUser.firstName}
            onChange={(e) => setCurrentUser({ ...currentUser, firstName: e.target.value })}
            placeholder="Enter first name"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={currentUser.lastName}
            onChange={(e) => setCurrentUser({ ...currentUser, lastName: e.target.value })}
            placeholder="Enter last name"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            value={currentUser.email}
            onChange={(e) => setCurrentUser({ ...currentUser, email: e.target.value })}
            placeholder="Enter email address"
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Role <span className="text-red-500">*</span>
          </label>
          <Select
            value={currentUser.roleId && currentUser.roleId > 0 ? currentUser.roleId.toString() : undefined}
            onValueChange={(value) => setCurrentUser({ ...currentUser, roleId: parseInt(value) })}
            disabled={roles.length === 0}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={roles.length === 0 ? "No roles available" : "Select a role"} />
            </SelectTrigger>
            <SelectContent>
              {roles.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-gray-500">No roles available</div>
              ) : (
                roles.map((role) => (
                  <SelectItem key={role.id} value={role.id.toString()}>
                    {role.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone
          </label>
          <PhoneInput
            value={currentUser.phone || ''}
            onChange={(value) => setCurrentUser({ ...currentUser, phone: value })}
            className="w-full"
            placeholder="Enter phone number"
            id="phone"
            name="phone"
          />
        </div>
      </div>

      <div className="flex gap-2 mb-8">
        <Button
          variant="outline"
          onClick={handleInviteUser}
          disabled={
            !currentUser.email.trim() ||
            !currentUser.firstName.trim() ||
            !currentUser.lastName.trim() ||
            !currentUser.roleId ||
            currentUser.roleId === 0 ||
            createUserMutation.isPending
          }
        >
          {createUserMutation.isPending ? 'Inviting...' : 'Invite User'}
        </Button>
        {invitedUsers.length > 0 && (
          <Button
            variant="outline"
            onClick={() =>
              setCurrentUser({
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                roleId: 0,
                password: generateTempPassword(),
              })
            }
          >
            Clear Form
          </Button>
        )}
      </div>

      <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" onClick={onSkip}>
          Skip for Now
        </Button>
        <Button onClick={handleContinue} disabled={invitedUsers.length === 0}>
          Continue →
        </Button>
      </div>
    </div>
  );
};
